import React, { useState, useMemo, useContext } from "react";
import { Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import DataTable from "react-data-table-component";
import { NotificationManager } from "react-notifications";
import { makeStyles } from "@mui/styles";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";
import { deleteMarketMembershipFile, downloadFile } from "services/ConfigServices";
import { membercontractStatusDataTab } from "variables/lists/markets";
import useContractTable from "./useContractTable.js";
import EditContractDialog from "./EditContractDialog";
import EditContractStatusDialog from "./EditContractStatusDialog";
import CustomDeleteButton from "components/CustomDeleteButton/CustomDeleteButton.js";
import UserService from "services/UserService";
import Button from "components/CustomButtons/Button.js";
import styled from "styled-components";
import AdvancedSearchContext from "context/AdvancedSearchContext";
import { membercontracts } from "variables/markets";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import { withStyles } from "@mui/styles";
import MuiDialogContent from "@mui/material/DialogContent";
import MuiDialogActions from "@mui/material/DialogActions";
import CustomDialogTitle from "components/Dialog/CustomDialogTitle.js";
import MarketsContext from "context/MarketsContext";


const GroupCell = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Container = styled.div`
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.15);
  border-top: 4px solid rgba(255, 255, 255, 1);
  background-color: white;
  margin-top: -0.5px;
`;

const ExpandableContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 4rem 2rem;
`;
const ExpandableElement = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: ${(props) => props.marginleft};
`;

const ElementTitle = styled.div`
  font-weight: 600;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const ElementData = styled.div`
  margin-left: 4px;
  font-weight: 400;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const NoData = styled.p`
  font-size: 14px;
  font-weight: 600;
  margin-top: 20px;
  opacity: 1;
`;

const ElementDataDownload = styled.div`
  margin-left: 4px;
  font-weight: 400;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
  cursor: pointer;
  text-decoration: underline;
`;

const DialogContent = withStyles((theme) => ({
  root: {
    padding: '16px',
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: '8px',
  },
}))(MuiDialogActions);

const ExpandableComponent = props => {
  const { data } = props;
  console.log(data);
  return (
    <Container>
      <ExpandableContainer>
        <ExpandableElement>
          <ElementTitle>{membercontracts.legalentity} : </ElementTitle>
          <ElementData>{data.parameters?.legalentity}</ElementData>
        </ExpandableElement>
        <ExpandableElement>
          <ElementTitle>{membercontracts.representedby} : </ElementTitle>
          <ElementData>{data.parameters?.representedby}</ElementData>
        </ExpandableElement>
        { data.parameters?.associatedfiles && 
          <ExpandableElement>
          <ElementTitle>{membercontracts.associatedfiles} : </ElementTitle>
          <ElementDataDownload onClick={downloadGED(data.parameters?.associatedfiles.fileId, data.parameters?.associatedfiles.fileName)}>{data.parameters?.associatedfiles.fileName}</ElementDataDownload>
        </ExpandableElement>
        }
      </ExpandableContainer>
    </Container>
  );
};

const downloadGED = (fileId, fileName) => async () => {
  axios
    .get(
      downloadFile(fileId),
      {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${UserService.getToken()}`,
        },
      }
    )
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName); //or any other extension
      document.body.appendChild(link);
      link.click();
    });
};


const useStyles = makeStyles(styles);

const RTContracts = () => {
  const permis = JSON.parse(localStorage.getItem("permission")) || [];
  const markets_update = permis.includes("markets_update");
  const { gridState, refetch, setSelectedContracts } = useContractTable();
  const marketContext = useContext(MarketsContext);
  const { market, setMarket } = marketContext;
  const advancedSearchContext = useContext(AdvancedSearchContext);
  const { groupInfoListById, setPage } = advancedSearchContext;
  const [open, setOpen] = useState(false);
  const [openStatus, setOpenStatus] = useState(false);
  const [currentMemberFile, setCurrentMemberFile] = useState(null);

  let dataMembers = gridState.data;

  const classes = useStyles();

  const columns = [
    {
      name: membercontracts.docnumber,
      selector: (row) => row.parameters.docnumber,
      sortable: true,
      maxWidth: "13rem",
    }, 
    {
      name: membercontracts.docname,
      selector: (row) => row.parameters.docname,
      sortable: true,
      maxWidth: "13rem",
    },
    {
      name: membercontracts.status,
      sortable: true,
      maxWidth: "12rem",
      cell: (row) => row.parameters.status ?
        <div style={{ color: membercontractStatusDataTab.find((x) => x.id == row.parameters.status).color, fontWeight: "bold" }}>
          {membercontractStatusDataTab.find((x) => x.id == row.parameters.status).name}
        </div> : <></>
    }, 
    {
      name: membercontracts.marketmembershipnumber,
      selector: (row) => row.marketmembershipnumber,
      sortable: true,
      maxWidth: "9rem",
    }, 
    {
      name: membercontracts.marketmembershipname,
      selector: (row) => row.marketmembershipname,
      sortable: true,
      maxWidth: "9rem",
    },
    {
      name: membercontracts.datebegin,
      sortable: true,
      maxWidth: "10rem",
      cell: (row) =>
        row.parameters.datebegin && moment(row.parameters.datebegin).isValid()
          ? moment(row.parameters.datebegin).format("DD/MM/YYYY")
          : "",
    },
    {
      name: membercontracts.dateend,
      sortable: true,
      maxWidth: "10rem",
      cell: (row) =>
        row.parameters.dateend && moment(row.parameters.dateend).isValid()
          ? moment(row.parameters.dateend).format("DD/MM/YYYY")
          : "",
    },
    {
      name: membercontracts.datesignature,
      sortable: true,
      maxWidth: "10rem",
      cell: (row) =>
        row.parameters.datesignature && moment(row.parameters.datesignature).isValid()
          ? moment(row.parameters.datesignature).format("DD/MM/YYYY")
          : "",
    },
    {
      name: "",
      sortable: false,
      width: "10rem",
      // eslint-disable-next-line react/button-has-type
      // eslint-disable-next-line react/display-name
      cell: (row) => (
        <>
          <div className="actions-right">
            {
              markets_update && 
                <Tooltip title="Changer le statut">
                  <Button
                    onClick={editMemberFileStatus(row)}
                    color={"info"}
                    className={classes.actionButton}
                  >
                    <ExitToAppIcon fontSize="large" />
                  </Button>
                </Tooltip>
            }{markets_update && (
              <>
            <Tooltip title="Editer le contrat">
              <Button
                onClick={editMemberFile(row)}
                color={"warning"}
                className={classes.actionButton}
              >
                <EditIcon fontSize="large" />
              </Button>
            </Tooltip>
            
              <Tooltip title="Supprimer le contrat">
                <CustomDeleteButton
                  deleteTitle={"Suppression du contrat"}
                  deleteSubTitle={`Êtes-vous sûr de vouloir supprimer le contrat?`}
                  handleSupprimer={deleteMarketMembershipFileFunction(row)}
                />
              </Tooltip>
              </>
            )}
          </div>
        </>
      ),
    },
  ];

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleEditClose = () => {
    setOpen(false);
  };

  const handleClickStatusOpen = () => {
    setOpenStatus(true);
  };
  const handleEditStatusClose = () => {
    setOpenStatus(false);
  };

  const editMemberFile = (row) => async () => {
    let obj = dataMembers.find((o) => o.id === row.id);
    if (obj) {
      setCurrentMemberFile(obj);
      handleClickOpen();
    }
  }

  const editMemberFileStatus = (row) => async () => {
    let obj = dataMembers.find((o) => o.id === row.id);
    if (obj) {
      setCurrentMemberFile(obj);
      handleClickStatusOpen();
    }
  }

  const deleteMarketMembershipFileFunction = (row) => async () => {
    let obj = dataMembers.find((o) => o.id === row.id);
    let idSelected = obj.id;
    let url = deleteMarketMembershipFile(idSelected);
    axios
      .delete(url, {
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
        },
      })
      .then((res) => {
        if (res.status == 200) {
          setNotification(
            "success",
            `Le contrat a été supprimé`
          );
          refetch();
        } else setNotification("error", "Une erreur est survenue");
      })
      .catch((error) => {
        setNotification("error", "Une erreur est survenue");
      });
  };

  const setNotification = (status, text) => {
    if (status === "success") {
      NotificationManager.success(text);
    } else {
      NotificationManager.error(text);
    }
  };

  const handleSelectedRowsChange = (selection) => {
    setSelectedContracts(selection.selectedRows);
  };

  return (
    <>
      <DataTable
          paginationRowsPerPageOptions={[10,20,30,50,100]}
        noDataComponent={<NoData>Aucun contrat à afficher</NoData>}
        noHeader={true}
        columns={columns}
        persistTableHead
        pagination
        subHeader
        progressPending={gridState.loading}
        progressComponent={<LinearProgress />}
        onSelectedRowsChange={handleSelectedRowsChange}
        selectableRows={true}
        expandOnRowClicked
        expandableRows
        expandableRowDisabled={(row) => row.disabled}
        expandableRowsComponent={ExpandableComponent}
        paginationComponentOptions={{
          noRowsPerPage: false,
          rowsPerPageText: "Lignes par page",
          rangeSeparatorText: "sur",
          selectAllRowsItem: false,
          selectAllRowsItemText: "Selectionner toutes les lignes",
        }}
        {...gridState}
      />
      <Dialog
        onClose={handleEditClose}
        aria-labelledby="edituser-dialog-title"
        fullWidth="true"
        maxWidth="sm"
        open={open}
      >
        <CustomDialogTitle id="customized-dialog-title" onClose={handleEditClose}>
          Modification du contrat
        </CustomDialogTitle>
        <DialogContent dividers>
          <EditContractDialog
            membershipfile={currentMemberFile}
            closeModal={handleEditClose}
            refetch={refetch}
          />
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
      <Dialog
        onClose={handleEditStatusClose}
        aria-labelledby="edituser-dialog-title"
        fullWidth="true"
        maxWidth="sm"
        open={openStatus}
      >
        <CustomDialogTitle id="customized-dialog-title" onClose={handleEditStatusClose}>
          Modification du statut d'un contrat
        </CustomDialogTitle>
        <DialogContent dividers>
          <EditContractStatusDialog
            membershipfile={currentMemberFile}
            closeModal={handleEditStatusClose}
            refetch={refetch}
          />
        </DialogContent>
        <DialogActions></DialogActions>
      </Dialog>
    </>
  );
};

export default RTContracts;
